/**
 * @file TradingBody.jsx
 * @description Provides main trading interface and functionality
 *
 * @section Responsibilities
 * - Display trading interface
 * - Handle trading operations
 * - Manage trading state
 * - Process trading data
 * - Handle trading errors
 * - Provide trading controls
 *
 * @section Dependencies
 * - TradingService: Trading operations
 * - DataService: Data processing
 * - StateService: State management
 * - ControlService: Control handling
 *
 * @section Integration Points
 * - Trading API: Trading operations
 * - Data System: Data processing
 * - State System: State management
 * - Control System: Trading controls
 *
 * @section State Management
 * - tradingState: Trading status
 * - operationState: Operation status
 * - dataState: Data status
 * - errorState: Error handling
 *
 * @section Events
 * - onTrade: Trading operations
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Object} tradingConfig - Trading configuration
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialData - Initial trading data
 * @prop {Boolean} autoTrade - Controls auto-trading
 */

import React, { useState, useEffect } from "react";
import {
  OrderSides,
  OrderTypes,
  OrderDurations,
  OrderOptionTypes,
  OrderClasses,
} from "./tradingVars";

function TradingBody({
  orderClass,
  data,
  setOrderBody,
  getExpiration,
  prefilledOrder,
  updatedStrikes,
}) {
  const ORDER_TYPES = {
    MARKET: "market",
    LIMIT: "limit",
    STOP: "stop",
    STOP_LIMIT: "stop_limit",
    DEBIT: "debit",
    CREDIT: "credit",
    EVEN: "even",
  };

  // const [order, setOrder, clearOrder] = useForm({...orderData});

  const [side, setSide] = useState("");
  const [quantity, setQuantity] = useState("");
  const [type, setType] = useState("");
  const [duration, setDuration] = useState("");
  const [tag, setTag] = useState("tag");

  const [expiration, setExpiration] = useState();
  const [strike, setStrike] = useState();

  const [stop, setStop] = useState();
  const [price, setPrice] = useState();

  const [optionType, setOptionType] = useState();
  const [optionSymbol, setOptionSymbol] = useState();

  let orderData = {
    class: orderClass,
    symbol: data.symbol,
    side: side,
    quantity: quantity,
    type: type,
    duration: duration,
    tag: tag,
    stop: stop,
    price: price,
    option_symbol: optionType,
  };

  useEffect(() => {
    if (type === ORDER_TYPES.MARKET || type === ORDER_TYPES.EVEN) {
      delete orderData.stop;
      delete orderData.price;
    } else if (
      type === ORDER_TYPES.LIMIT ||
      type === ORDER_TYPES.DEBIT ||
      type === ORDER_TYPES.CREDIT
    ) {
      delete orderData.stop;
    } else if (type === ORDER_TYPES.STOP) {
      delete orderData.price;
    }

    if (orderClass != OrderClasses.OPTION.value) {
      delete orderData.option_symbol;
    }

    setOrderBody(orderData);
  }, [side, quantity, type, duration, tag, stop, price, optionType]);

  useEffect(() => {
    setSide("");
    setQuantity("");
    setType("");
    setDuration("");
    setStop("");
    setPrice("");
    setExpiration("");
    setStrike("");
    setOptionType("");
  }, [data]);

  useEffect(() => {
    if (prefilledOrder) {
      setSide(prefilledOrder.side);
      setQuantity(prefilledOrder.quantity);
      setType(OrderTypes.MARKET.value);
      setDuration(OrderDurations.DAY.value);
      orderClass = "equity";
    }
  }, [prefilledOrder]);

  useEffect(() => {
    getExpiration(expiration);
  }, [expiration]);

  useEffect(() => {
    if (optionType) {
      const [year, month, day] = expiration.split("-");
      const shortYear = year.slice(-2);

      setOptionType();

      orderData.option_symbol = `${shortYear}${month}${day}${optionType}`;
    }
  }, [optionType]);

  const OptionTrade = () => (
    <div className="row">
      <div className="form-field">
        <select
          name=""
          id=""
          onChange={(e) => setExpiration(e.target.value)}
          value={expiration}
        >
          <option value="">Expirations</option>
          {data?.expirations?.date &&
            Object.keys(data?.expirations?.date).map((expiration, index) => (
              <option key={data.expirations.date[index]}>
                {data.expirations.date[index]}
              </option>
            ))}
        </select>
      </div>
      <div className="form-field">
        <select
          name=""
          id=""
          disabled={!updatedStrikes}
          onChange={(e) => setStrike(e.target.value)}
          value={strike}
        >
          <option value="">Strike</option>
          {updatedStrikes &&
            updatedStrikes.map((strike, index) => (
              <option key={index}>{strike}</option>
            ))}
        </select>
      </div>
      <div className="form-field">
        <select
          name=""
          id=""
          disabled={!strike}
          onChange={(e) => setOptionType(e.target.value)}
          value={optionType}
        >
          <option value="">Type</option>
          {Object.keys(OrderOptionTypes).map((opType, index) => (
            <option
              key={OrderOptionTypes[opType].value}
              value={OrderOptionTypes[opType].value}
            >
              {OrderOptionTypes[opType].name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const MultilegTrade = () => (
    <div className="row-leg">
      <div className="row">
        <div className="form-field">
          <select
            name=""
            id=""
            onChange={(e) => setExpiration(e.target.value)}
            value={expiration}
          >
            <option value="">Expirations</option>
            {data?.expirations?.date &&
              Object.keys(data?.expirations?.date).map((expiration, index) => (
                <option key={data.expirations.date[index]}>
                  {data.expirations.date[index]}
                </option>
              ))}
          </select>
        </div>
        <div className="form-field">
          <select name="" id="" disabled={!expiration}>
            <option value="">Strike</option>
          </select>
        </div>
        <div className="form-field">
          <select name="" id="" disabled={!strike}>
            <option value="">Type</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {orderClass === "option" ? (
        <OptionTrade />
      ) : orderClass === "multileg" ? (
        <MultilegTrade />
      ) : null}
      <div className="row">
        <div className="form-field">
          <select
            name=""
            id=""
            onChange={(e) => setSide(e.target.value)}
            value={side}
          >
            <option value="">Side</option>
            {Object.keys(data).length !== 0 &&
              Object.keys(OrderSides).map((oSide) => (
                <option
                  key={OrderSides[oSide].value}
                  value={OrderSides[oSide].value}
                >
                  {OrderSides[oSide].name}
                </option>
              ))}
          </select>
        </div>
        <div className="form-field">
          <input
            type="number"
            placeholder="Quantity"
            value={quantity}
            onChange={(e) => {
              setQuantity(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="row">
        <div className="form-field">
          <select
            name=""
            id=""
            onChange={(e) => setType(e.target.value)}
            value={type}
          >
            <option value="">Type</option>
            {Object.keys(data).length !== 0 &&
              Object.keys(OrderTypes).map((oType) => (
                <option
                  key={OrderTypes[oType].value}
                  value={OrderTypes[oType].value}
                >
                  {OrderTypes[oType].name}
                </option>
              ))}
          </select>
        </div>
        {(orderClass === "equity" || orderClass === "option") && (
          <>
            {type === "limit" && (
              <div className="form-field">
                <input
                  type="number"
                  placeholder="Price"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                  }}
                />
              </div>
            )}
            {type === "stop" && (
              <div className="form-field">
                <input
                  type="number"
                  placeholder="Stop"
                  value={stop}
                  onChange={(e) => {
                    setStop(e.target.value);
                  }}
                />
              </div>
            )}
            {type === "stop_limit" && (
              <>
                <div className="form-field">
                  <input
                    type="number"
                    placeholder="Stop"
                    value={stop}
                    onChange={(e) => {
                      setStop(e.target.value);
                    }}
                  />
                </div>
                <div className="form-field">
                  <input
                    type="number"
                    placeholder="Limit"
                    value={price}
                    onChange={(e) => {
                      setPrice(e.target.value);
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
        <div className="form-field">
          <select
            name=""
            id=""
            onChange={(e) => setDuration(e.target.value)}
            value={duration}
          >
            <option value="">Duration</option>
            {Object.keys(data).length !== 0 &&
              Object.keys(OrderDurations).map((oDuration) => (
                <option
                  key={OrderDurations[oDuration].value}
                  value={OrderDurations[oDuration].value}
                >
                  {OrderDurations[oDuration].name}
                </option>
              ))}
          </select>
        </div>
      </div>
    </>
  );
}

export default TradingBody;
